import {
  Box,
  Button,
  MenuItem,
  Popover,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { setIsBookmarkSidebar, setSubPopup } from "context/features/dataSlice";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";

const TAB_SEGMENT_PATTERN = /\/(work-cited|collection-notes|teacher-notes)(\/.*)?$/;

const BookmarkHeader = ({ isArchived = false, isSubmitted = false, isTeacherMode = false }) => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const location = useLocation();
  const theme = useTheme();
  const navigate = useNavigate();

  const { collectionIdArray } = useSelector((state) => state.data);
  const [anchorEl, setAnchorEl] = useState(null);
  const isPopoverOpen = Boolean(anchorEl);

  const getSelectedOption = () => {
    const path = location.pathname;
    if (path.includes("/work-cited")) return "workcited";
    if (path.includes("/collection-notes")) return "collectionnotes";
    if (path.includes("/teacher-notes")) return "teachernotes";
    return "bookmarks";
  };

  const selectedOption = getSelectedOption();
  // In teacher mode the base is the submission route itself, so strip whichever tab
  // segment is currently on the URL (work-cited may carry further path segments).
  const basePath = isTeacherMode
    ? location.pathname.replace(TAB_SEGMENT_PATTERN, "")
    : isArchived
      ? `/collections/archive/${id}`
      : `/collections/${id}`;
  const teacherNotesPath = isTeacherMode
    ? `${basePath}/teacher-notes`
    : `/collections/${id}/teacher-notes`;

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const getBookmarkSxProps = (option) => ({
    fontStyle: "italic",
    cursor: "pointer",
    textDecoration: selectedOption === option ? "underline" : "none",
    textDecorationThickness: "2px",
    textUnderlineOffset: "8px",
    color: selectedOption === option ? theme.palette.primary.main : theme.palette.text.primary,
  });

  const handlePopoverOpen = (e) => {
    setAnchorEl(e.currentTarget);
  };

  return (
    <Stack my={2} direction="row" alignItems="center">
      <Stack mb="8px" direction="row" justifyContent="center" gap={3}>
        <Link style={{ textDecoration: "none" }} to={basePath} state={location.state}>
          <Typography variant="h5" sx={getBookmarkSxProps("bookmarks")}>
            Bookmarks
          </Typography>
        </Link>
        <Link
          style={{ textDecoration: "none" }}
          to={`${basePath}/work-cited`}
          state={location.state}
        >
          <Typography variant="h5" sx={getBookmarkSxProps("workcited")}>
            Works Cited
          </Typography>
        </Link>
        <Link
          style={{ textDecoration: "none" }}
          to={`${basePath}/collection-notes`}
          state={location.state}
        >
        <Typography sx={getBookmarkSxProps("collectionnotes")} variant="h5">
            Collection Notes
          </Typography>
        </Link>
        {isSubmitted && (
          <Link
            style={{ textDecoration: "none" }}
            to={teacherNotesPath}
            state={location.state}
          >
            <Typography sx={getBookmarkSxProps("teachernotes")} variant="h5">
              Teacher&apos;s Notes
            </Typography>
          </Link>
        )}
      </Stack>
      {!isArchived && !isTeacherMode && (
      <Box>
        <Button
          size="large"
          startIcon={<AddIcon />}
          endIcon={<KeyboardArrowDownIcon />}
          onClick={handlePopoverOpen}
          sx={{ ml: 2 }}
        >
          Create
        </Button>
        <Popover
          anchorEl={anchorEl}
          open={isPopoverOpen}
          onClose={handlePopoverClose}
          transformOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          anchorOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
        >
          <MenuItem
            sx={{
              p: 2,
              "&:hover": {
                backgroundColor: theme.palette.secondary.main,
              },
            }}
            onClick={() => {
              dispatch(setIsBookmarkSidebar(true));
              handlePopoverClose();
            }}
          >
            <Typography variant="body1">Create External Bookmark</Typography>
          </MenuItem>
          <MenuItem
            onClick={() => navigate(`${basePath}/work-cited/create`)}
            sx={{
              p: 2,
              "&:hover": {
                backgroundColor: theme.palette.secondary.main,
              },
            }}
          >
            <Typography variant="body1"> Create Works Cited</Typography>
          </MenuItem>
          {collectionIdArray.length < 3 && (
            <MenuItem
              onClick={() => {
                dispatch(setSubPopup(true));
                handlePopoverClose();
              }}
              sx={{
                p: 2,
                "&:hover": {
                  backgroundColor: theme.palette.secondary.main,
                },
              }}
            >
              <Typography variant="body1">{`Create Subfolder`}</Typography>
            </MenuItem>
          )}
        </Popover>
      </Box>
      )}
    </Stack>
  );
};

export default BookmarkHeader;
